import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import { getIcon } from '../../utils/iconMapper';
import RolForm from './RolForm';
import { rolesService } from '../../services/rolesService';
import SnowFallComponent from 'components/SnowFallComponent';

function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRol, setEditingRol] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rolToDelete, setRolToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [soloActivos, setSoloActivos] = useState(true);

  useEffect(() => {
    loadRoles();
  }, [soloActivos]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await rolesService.getAll(soloActivos);

      if (response.success && response.data) {
        const rolesData = Array.isArray(response.data) ? response.data : [];
        const normalized = rolesData.map(rol => ({
          RolID: rol.RolID || rol.rolID,
          Nombre: rol.Nombre || rol.nombre || '',
          Descripcion: rol.Descripcion || rol.descripcion,
          Activo: rol.Activo !== undefined ? rol.Activo : (rol.activo !== undefined ? rol.activo : true),
          FechaCreacion: rol.FechaCreacion || rol.fechaCreacion,
        }));
        setRoles(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar roles');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRol(null);
    setShowForm(true);
  };

  const handleEdit = async (rol) => {
    try {
      setLoading(true);
      const response = await rolesService.getById(rol.RolID || rol.rolID);
      if (response.success && response.data) {
        setEditingRol(response.data);
        setShowForm(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar el rol');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (rol) => {
    setRolToDelete(rol);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const rolId = rolToDelete.RolID || rolToDelete.rolID;
      const response = await rolesService.delete(rolId);

      if (response.success) {
        setSuccess('Rol desactivado exitosamente');
        setTimeout(() => setSuccess(''), 3000);
        setShowDeleteModal(false);
        setRolToDelete(null);
        loadRoles();
      } else {
        setError(response.Message || response.message || 'Error al desactivar el rol');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingRol(null);
    setSuccess('Rol guardado exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    loadRoles();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingRol(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      accessor: 'Nombre',
    },
    {
      key: 'descripcion',
      label: 'Descripción',
      accessor: 'Descripcion',
      render: (value) => value || '-',
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'danger'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'fechaCreacion',
      label: 'Fecha Creación',
      accessor: 'FechaCreacion',
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => (
        <div>
          <Button
            color="info"
            size="sm"
            className="mr-2"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
            title="Editar"
          >
            <i className={getIcon("tim-icons icon-pencil")} />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row);
            }}
            title="Desactivar"
            disabled={!row.Activo}
          >
            <i className={getIcon("tim-icons icon-simple-remove")} />
          </Button>
        </div>
      ),
    },
  ];

  if (showForm) {
    return (
      <RolForm
        rol={editingRol}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Roles</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button color="primary" onClick={handleCreate}>
                    <i className={getIcon("tim-icons icon-simple-add")} /> Nuevo Rol
                  </Button>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="3">
                  <label>
                    <input
                      type="checkbox"
                      checked={soloActivos}
                      onChange={(e) => setSoloActivos(e.target.checked)}
                      className="mr-2"
                    />
                    Solo activos
                  </label>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={roles} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setRolToDelete(null);
        }}
        title="Desactivar Rol"
        message="¿Está seguro de desactivar este rol? El rol no podrá ser asignado pero sus datos se mantendrán."
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Roles;

